import { mulaw } from "alawmulaw";
import {
    MediaStreamConfig,
    MediaStreamSDKOptions,
} from "./types/MediaStreamSDK";
import { downsampleBuffer } from "./utils/audioUtils";
import {
    closeWebSocketConnection,
    createWebSocketConnection,
    sendMessage,
} from "./utils/websocketUtils";

export default class MediaStreamSDK {
  private readonly websocketUrl: string;
  private readonly streamSid: string;
  private readonly audioSampleRate: number;
  private socket: WebSocket | null = null;
  private inputAudioContext: AudioContext | null = null;
  private outputAudioContext: AudioContext | null = null;
  private audioQueue: Float32Array[] = [];
  private isPlaying = false;
  private readonly onError?: (error: Error) => void;
  private readonly onClose?: () => void;
  private lastAudioEndTime: number = 0;
  private readonly audioMarksQueue: string[] = [];

  constructor(config: MediaStreamConfig, options?: MediaStreamSDKOptions) {
    this.websocketUrl = config.websocketUrl;
    this.streamSid = config.streamSid;
    this.audioSampleRate = config.audioSampleRate ?? 24000;
    this.onError = options?.onError;
    this.onClose = options?.onClose;
  }

  async startConversation() {
    try {
      await this.startCall();
    } catch (error) {
      console.error("Error starting conversation:", error);
      this.onError?.(error as Error);
    }
  }

  stopConversation() {
    this.stopCall();
  }

  createWebSocketConnection() {
    this.createWebSocket();
  }

  destroyWebSocketConnection() {
    if (this.socket) {
      closeWebSocketConnection(this.socket);
      this.socket = null;
    }
  }

  private createWebSocket() {
    this.socket = createWebSocketConnection({
      websocketUrl: this.websocketUrl,
      onOpen: (socket) => {
        socket.send(
          JSON.stringify({
            event: "start",
            start: { streamSid: this.streamSid },
          })
        );
        console.log("WebSocket connection established.");
      },
      onMessage: (socket, event) => {
        const message = JSON.parse(event.data);
        if (message.event === "mark" && message.mark?.name) {
          this.audioMarksQueue.push(message.mark.name);
        } else if (message.event === "media" && message.media?.payload) {
          this.enqueueAudio(message.media.payload);
        } else if (message.event === "clear") {
          this.clearAudioQueueAndSendMarks();
        }
      },
      onError: (error) => {
        console.error("WebSocket error:", error);
        this.onError?.(new Error("WebSocket connection error."));
      },
      onClose: () => {
        console.log("WebSocket connection closed.");
        this.onClose?.();
      },
    });
  }

  private enqueueAudio(base64Audio: string) {
    const audioBytes = new Uint8Array(
      atob(base64Audio)
        .split("")
        .map((char) => char.charCodeAt(0))
    );
    const pcmData = mulaw.decode(audioBytes);

    // Convert PCM data to Float32Array
    const float32Data = new Float32Array(pcmData.length);
    for (let i = 0; i < pcmData.length; i++) {
      float32Data[i] = Math.max(-1, Math.min(1, pcmData[i] / 32767));
    }

    this.audioQueue.push(float32Data);

    if (!this.isPlaying) {
      this.isPlaying = true;
      this.processQueue();
    }
  }

  private processQueue() {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    const chunksToPlay = this.audioQueue.splice(0, this.audioQueue.length);
    const totalLength = chunksToPlay.reduce(
      (sum, chunk) => sum + chunk.length,
      0
    );

    if (totalLength === 0) {
      this.processQueue();
      return;
    }

    const concatenatedAudio = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunksToPlay) {
      concatenatedAudio.set(chunk, offset);
      offset += chunk.length;
    }

    if (!this.outputAudioContext) {
      this.outputAudioContext = new AudioContext({ sampleRate: 8000 });
    }
    if (!concatenatedAudio.length) return;

    const audioBuffer = this.outputAudioContext.createBuffer(
      1,
      concatenatedAudio.length,
      8000
    );
    audioBuffer.copyToChannel(concatenatedAudio, 0);

    const source = this.outputAudioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.outputAudioContext.destination);

    const startTime = Math.max(
      this.outputAudioContext.currentTime,
      this.lastAudioEndTime
    );
    source.start(startTime);
    this.lastAudioEndTime = startTime + audioBuffer.duration;

    source.onended = () => {
      if (this.audioMarksQueue.length > 0) {
        const markName = this.audioMarksQueue.shift();
        if (markName) {
          this.sendAudioMark(markName);
        }
      }

      this.processQueue();
    };
  }

  private clearAudioQueueAndSendMarks() {
    if (this.outputAudioContext) {
      this.outputAudioContext.close();
      this.outputAudioContext = null;
    }
    this.audioQueue = [];
    this.isPlaying = false;
    this.lastAudioEndTime = 0;

    // Send all queued marks
    while (this.audioMarksQueue.length > 0) {
      const markName = this.audioMarksQueue.shift();
      if (markName) {
        this.sendAudioMark(markName);
      }
    }
  }

  private sendAudioMark(mark: string) {
    if (this.socket) {
      sendMessage(this.socket, {
        event: "mark",
        streamSid: this.streamSid,
        mark: { name: mark },
      });
    }
  }

  private async startCall() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: this.audioSampleRate },
      });

      this.inputAudioContext = new ((window as any).AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: this.audioSampleRate,
      });

      if (!this.inputAudioContext) {
        throw new Error("Failed to create AudioContext.");
      }

      if (!this.socket) {
        console.log("Creating WebSocket connection...");
        this.createWebSocket();
      }

      await this.inputAudioContext.audioWorklet.addModule(
        "/worklet-processor.js"
      );
      const workletNode = new AudioWorkletNode(
        this.inputAudioContext,
        "processor"
      );
      const sourceNode = this.inputAudioContext.createMediaStreamSource(stream);
      sourceNode.connect(workletNode);
      workletNode.connect(this.inputAudioContext.destination);

      workletNode.port.onmessage = (event) => {
        const inputBuffer = event.data;
        const downsampledBuffer = downsampleBuffer(inputBuffer, 24000, 8000);

        // Convert the downsampled buffer to PCM format (Int16)
        const pcmData = new Int16Array(downsampledBuffer.length);
        for (let i = 0; i < downsampledBuffer.length; i++) {
          pcmData[i] = Math.max(-1, Math.min(1, downsampledBuffer[i])) * 0x7fff;
        }

        // Encode PCM data to µ-law
        const encodedMulaw = mulaw.encode(pcmData);

        // Convert encoded µ-law data to Base64
        const base64Audio = btoa(
          String.fromCharCode(...new Uint8Array(encodedMulaw))
        );

        // Send the audio data over WebSocket
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          const mediaMessage = JSON.stringify({
            event: "media",
            media: {
              payload: base64Audio,
              timestamp: Date.now(),
            },
          });

          this.socket.send(mediaMessage);
        } else {
          console.error("WebSocket is not open.");
        }
      };
    } catch (error) {
      console.error("Error starting call:", error);
      this.onError?.(error as Error);
    }
  }

  private stopCall() {
    if (this.inputAudioContext) {
      this.inputAudioContext.close();
      this.inputAudioContext = null;
    }
    if (this.outputAudioContext) {
      this.outputAudioContext.close();
      this.outputAudioContext = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.clearAudioQueueAndSendMarks();
    this.lastAudioEndTime = 0;
    this.audioMarksQueue.length = 0;
    this.isPlaying = false;
    console.log("Call stopped.");
  }
}
